import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useRouteNavigate } from '../../../../hooks';
import bundleDiscountOffer from '../../../../static/img/bundle-banner/bundle-discount-offer.jpeg';
import { BannerImage, DiscountModal } from './BundleDiscountModal.styled';

export const BUNDLE_DISCOUNT_MODAL_SESSION_KEY = 'bundleDiscountModalShown';

const BUNDLES_PATH = '/buy-bundles';
// Offer ends 31 Oct 2026 11:59 PM PKT; the modal never renders from this instant onwards.
const OFFER_END_TIME = Date.parse('2026-11-01T00:00:00+05:00');

const isOfferActive = () => Date.now() < OFFER_END_TIME;

const wasShownThisSession = () => {
  try {
    return sessionStorage.getItem(BUNDLE_DISCOUNT_MODAL_SESSION_KEY) === 'true';
  } catch {
    return false;
  }
};

const markShownThisSession = () => {
  try {
    sessionStorage.setItem(BUNDLE_DISCOUNT_MODAL_SESSION_KEY, 'true');
  } catch {}
};

/**
 * Login pop-up for marketing's credit-bundle discount offer. Shown once per session
 * to bundle-eligible Zameen users on desktop until the offer ends.
 */
const BundleDiscountModal = () => {
  const { t } = useTranslation();
  const navigate = useRouteNavigate();
  const { pathname } = useLocation();
  const { user } = useSelector((state) => state.app.loginUser);
  const { isMobile } = useSelector((state) => state.app.AppConfig);
  const [isVisible, setIsVisible] = useState(false);

  // Desktop only: the offer banner is not shown on mobile devices.
  const isEligible = !!user?.eligible_for_bundles && !isMobile;

  useEffect(() => {
    if (!isEligible || !isOfferActive() || wasShownThisSession()) return;

    // Landing straight on Buy Bundles counts as this session's view.
    if (pathname.includes(BUNDLES_PATH)) {
      markShownThisSession();
      return;
    }

    let cancelled = false;
    const image = new Image();
    image.onload = () => {
      markShownThisSession();
      // Re-check in case the offer ended while the image was loading.
      !cancelled && isOfferActive() && setIsVisible(true);
    };
    // Never open an empty modal: skip the pop-up for this session if the banner fails.
    image.onerror = markShownThisSession;
    image.src = bundleDiscountOffer;

    return () => {
      cancelled = true;
    };
  }, [isEligible]);

  const handleClose = () => setIsVisible(false);

  const goToBundles = () => {
    handleClose();
    navigate(BUNDLES_PATH);
  };

  if (!isEligible || !isOfferActive()) return null;

  return (
    <DiscountModal
      visible={isVisible}
      onCancel={handleClose}
      footer={null}
      width="min(760px, calc(100vw - 32px))"
      centered
      destroyOnClose
      className="bundle-discount-modal"
    >
      <BannerImage
        src={bundleDiscountOffer}
        alt={t(
          'Last chance: discounts on Hot, Super Hot and Refresh credit bundles. Starter 10%, Business 12.5%, Titanium 15% off.',
        )}
        role="link"
        tabIndex={0}
        onClick={goToBundles}
        onKeyDown={(e) => e.key === 'Enter' && goToBundles()}
      />
    </DiscountModal>
  );
};

export default BundleDiscountModal;
